'use client'

import { createContext, useContext, useState, useEffect } from 'react'
import { config } from '@/config/config'
import { useLanguage } from './LanguageProvider'
import { useTranslation } from '@/hooks/useTranslation'


interface ClientCountryContextType {
  currentCountryData: { name: string, id: string } | null;
  isLoadingCurrentCountryData: boolean;
  changeCountryData: (countryData: { name: string, id: string } | null) => void;
}

const ClientCountryContext = createContext<ClientCountryContextType | undefined>(undefined)

const COUNTRY_STORAGE_KEY = 'selectedCountry'
const COUNTRY_COOKIE_NAME = config.app.countryCookieName

// Helper functions to manage cookies

// Calcula el dominio de la cookie para compartirla entre subdominios
// (p.ej. ".rockthesport.com" desde "web.rockthesport.com"). Así, si el usuario
// llega por otro subdominio y su localStorage está vacío, el fallback por cookie
// recupera el país en lugar de volver a mostrar el modal de selección.
// Devuelve null en localhost / IPs para dejar la cookie host-only en desarrollo.
const getCookieDomain = (): string | null => {
  if (typeof window === 'undefined') return null
  const hostname = window.location.hostname
  // localhost, IPv4 o hosts de una sola etiqueta => cookie host-only
  if (hostname === 'localhost' || /^[0-9.]+$/.test(hostname) || !hostname.includes('.')) {
    return null
  }
  // Dominio registrable = últimas dos etiquetas (válido para los .com/.app usados aquí)
  const registrableDomain = hostname.split('.').slice(-2).join('.')
  return `.${registrableDomain}`
}

const setCookie = (name: string, value: string, days: number = 365) => {
  const expires = new Date()
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000)
  const domain = getCookieDomain()
  const domainPart = domain ? `;domain=${domain}` : ''
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/${domainPart};SameSite=Lax`
}

const getCookie = (name: string): string | null => {
  const nameEQ = name + "="
  const ca = document.cookie.split(';')
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i]
    while (c.charAt(0) === ' ') c = c.substring(1, c.length)
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length)
  }
  return null
}

const deleteCookie = (name: string) => {
  const domain = getCookieDomain()
  const domainPart = domain ? `;domain=${domain}` : ''
  // Borrar la cookie compartida entre subdominios
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/${domainPart}`
  // Y también cualquier cookie host-only heredada de antes del cambio de dominio
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/`
}

export function ClientCountryProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<{ name: string, id: string } | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const { currentLocale } = useLanguage()
  const { t } = useTranslation()

  // Cargar valor del localStorage o cookie al inicializar
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedCountry = localStorage.getItem(COUNTRY_STORAGE_KEY)

      if (savedCountry) {
        try {
          const parsedCountry = JSON.parse(savedCountry)
          // Actualizar el nombre del país con la traducción del idioma actual
          const translatedName = t(`eventCountries.${parsedCountry.id}`)
          const updatedCountry = { ...parsedCountry, name: translatedName }
          setState(updatedCountry)
          // Ensure cookie is also set for SSR
          setCookie(COUNTRY_COOKIE_NAME, parsedCountry.id)
          // Actualizar localStorage con el nombre traducido
          localStorage.setItem(COUNTRY_STORAGE_KEY, JSON.stringify(updatedCountry))
        } catch (error) {
          console.error('[ClientCountryProvider] Error parsing saved country:', error)
          localStorage.removeItem(COUNTRY_STORAGE_KEY)
          deleteCookie(COUNTRY_COOKIE_NAME)
        }
      } else {
        // Try to load from cookie if localStorage is empty
        const cookieCountryId = getCookie(COUNTRY_COOKIE_NAME)
        if (cookieCountryId) {
          const translatedName = t(`eventCountries.${cookieCountryId}`)
          const countryData = { name: translatedName, id: cookieCountryId }
          setState(countryData)
          localStorage.setItem(COUNTRY_STORAGE_KEY, JSON.stringify(countryData))
        }
      }

      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Actualizar el nombre del país cuando cambia el idioma
  useEffect(() => {
    if (state && state.id && typeof window !== 'undefined' && !isLoading) {
      const translatedName = t(`eventCountries.${state.id}`)
      if (translatedName !== state.name) {
        setState(prevState => {
          if (!prevState) return prevState
          const updatedCountry = { ...prevState, name: translatedName }
          localStorage.setItem(COUNTRY_STORAGE_KEY, JSON.stringify(updatedCountry))
          return updatedCountry
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocale, state?.id, isLoading])

  const handleChangeCountryData = (countryData: { name: string, id: string } | null) => {
    // Guardar en localStorage y cookie (cookie para SSR, localStorage para persistencia)
    if (typeof window !== 'undefined') {
      setState(countryData)
      if (countryData) {
        localStorage.setItem(COUNTRY_STORAGE_KEY, JSON.stringify(countryData))
        // Also save in cookie so server can access it during SSR
        setCookie(COUNTRY_COOKIE_NAME, countryData.id)
        // Verify cookie was set
        const verifyCookie = getCookie(COUNTRY_COOKIE_NAME)
      } else {
        localStorage.removeItem(COUNTRY_STORAGE_KEY)
        deleteCookie(COUNTRY_COOKIE_NAME)
      }
    }
  }

  return (
    <ClientCountryContext.Provider value={{
      currentCountryData: state,
      isLoadingCurrentCountryData: isLoading,
      changeCountryData: handleChangeCountryData
    }}>
      {children}
    </ClientCountryContext.Provider>
  )
}

export function useClientCountry() {
  const context = useContext(ClientCountryContext)
  if (context === undefined) {
    throw new Error('useClientCountry must be used within a ClientCountryProvider')
  }
  return context
} 